import { combineReducers, configureStore, isRejectedWithValue, Middleware, ThunkDispatch } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta, setupListeners } from '@reduxjs/toolkit/dist/query';
import { complaintsApi } from './apiSlices/complaints.slice';
import { noticeApi } from './apiSlices/notices.slice';
import { integrationApi } from './apiSlices/integration.slice';
import { applicationApi } from './apiSlices/application.slice';
import { policyApi } from './apiSlices/policy.slice';
import { accessCodeApi } from './apiSlices/accessCode.slice';
import appSlice from './slice/app.slice';
import { storageApi } from './apiSlices/storage.slice';
import { residenceApi } from './apiSlices/residence.slice';
import { propertyApi } from './apiSlices/property.slice';
import { profileApi } from './apiSlices/profile.slice';
import { animalApi } from './apiSlices/animals.slice';
import { generalApi } from './apiSlices/generalApis.slice';
import { leasePacketApi } from './apiSlices/leasePacket.slice';
import { authApi } from './apiSlices/auth.slice';
import { userApi } from './apiSlices/user.slice';
import usersSlice from './slice/users.slice';
import { propertyManagementApi } from './apiSlices/propertyManagement.slice';
import { visitationApi } from './apiSlices/visitation.slice';
import { statesApi } from './apiSlices/states.slice';
import { roommatesApi } from './apiSlices/roommates.slice';
import { animalTrainingApi } from './apiSlices/animalTraining.slice';
import { docPacketApi } from './apiSlices/docPacket.slice';
import { commonApi } from './apiSlices/commonApis.slice';
import { propertyConfigSetApi } from './apiSlices/propertyConfigSet.slice';
import { paymentApi } from './apiSlices/payment.slice';
import { faqApi } from './apiSlices/faq.slice';
import { curriculumApi } from './apiSlices/curriculum.slice';
import { tenantApi } from './apiSlices/tenant.slice';
import { reportingApi } from './apiSlices/reporting.slice';
import { ssoApi } from './apiSlices/sso.slice';
import { partnerApiIntegrationsApi } from './apiSlices/partnerApiIntegrations.slice';
import { scimApi } from './apiSlices/scim.slice';

export const rootReducer = combineReducers({
  appSlice: appSlice.reducer,
  usersSlice: usersSlice.reducer,
  [complaintsApi.reducerPath]: complaintsApi.reducer,
  [noticeApi.reducerPath]: noticeApi.reducer,
  [integrationApi.reducerPath]: integrationApi.reducer,
  [propertyManagementApi.reducerPath]: propertyManagementApi.reducer,
  [applicationApi.reducerPath]: applicationApi.reducer,
  [policyApi.reducerPath]: policyApi.reducer,
  [accessCodeApi.reducerPath]: accessCodeApi.reducer,
  [storageApi.reducerPath]: storageApi.reducer,
  [residenceApi.reducerPath]: residenceApi.reducer,
  [propertyApi.reducerPath]: propertyApi.reducer,
  [profileApi.reducerPath]: profileApi.reducer,
  [animalApi.reducerPath]: animalApi.reducer,
  [generalApi.reducerPath]: generalApi.reducer,
  [leasePacketApi.reducerPath]: leasePacketApi.reducer,
  [visitationApi.reducerPath]: visitationApi.reducer,
  [authApi.reducerPath]: authApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [statesApi.reducerPath]: statesApi.reducer,
  [roommatesApi.reducerPath]: roommatesApi.reducer,
  [animalTrainingApi.reducerPath]: animalTrainingApi.reducer,
  [docPacketApi.reducerPath]: docPacketApi.reducer,
  [commonApi.reducerPath]: commonApi.reducer,
  [propertyConfigSetApi.reducerPath]: propertyConfigSetApi.reducer,
  [paymentApi.reducerPath]: paymentApi.reducer,
  [faqApi.reducerPath]: faqApi.reducer,
  [curriculumApi.reducerPath]: curriculumApi.reducer,
  [tenantApi.reducerPath]: tenantApi.reducer,
  [reportingApi.reducerPath]: reportingApi.reducer,
  [ssoApi.reducerPath]: ssoApi.reducer,
  [partnerApiIntegrationsApi.reducerPath]: partnerApiIntegrationsApi.reducer,
  [scimApi.reducerPath]: scimApi.reducer,
});

export const rtkQueryErrorLogger: Middleware = () => (next) => (action) => {
  if (isRejectedWithValue(action)) {
    console.error('We got a rejected action!', action);
  }
  return next(action);
};

export const setupStore = (initialState: Record<string, any> = {}) =>
  configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(
        complaintsApi.middleware,
        noticeApi.middleware,
        integrationApi.middleware,
        propertyManagementApi.middleware,
        applicationApi.middleware,
        policyApi.middleware,
        accessCodeApi.middleware,
        storageApi.middleware,
        residenceApi.middleware,
        propertyApi.middleware,
        profileApi.middleware,
        animalApi.middleware,
        generalApi.middleware,
        leasePacketApi.middleware,
        visitationApi.middleware,
        authApi.middleware,
        userApi.middleware,
        statesApi.middleware,
        roommatesApi.middleware,
        animalTrainingApi.middleware,
        docPacketApi.middleware,
        commonApi.middleware,
        propertyConfigSetApi.middleware,
        paymentApi.middleware,
        faqApi.middleware,
        curriculumApi.middleware,
        tenantApi.middleware,
        reportingApi.middleware,
        ssoApi.middleware,
        partnerApiIntegrationsApi.middleware,
        scimApi.middleware,
        rtkQueryErrorLogger
      ),
    preloadedState: initialState,
  });

export const store = setupStore();

export const clearAllCachedData = () => {
  store.dispatch(complaintsApi.util.resetApiState());
  store.dispatch(noticeApi.util.resetApiState());
  store.dispatch(residenceApi.util.resetApiState());
  store.dispatch(integrationApi.util.resetApiState());
  store.dispatch(propertyManagementApi.util.resetApiState());
  store.dispatch(applicationApi.util.resetApiState());
  store.dispatch(policyApi.util.resetApiState());
  store.dispatch(accessCodeApi.util.resetApiState());
  store.dispatch(storageApi.util.resetApiState());
  store.dispatch(propertyApi.util.resetApiState());
  store.dispatch(profileApi.util.resetApiState());
  store.dispatch(animalApi.util.resetApiState());
  store.dispatch(generalApi.util.resetApiState());
  store.dispatch(leasePacketApi.util.resetApiState());
  store.dispatch(authApi.util.resetApiState());
  store.dispatch(userApi.util.resetApiState());
  store.dispatch(visitationApi.util.resetApiState());
  store.dispatch(statesApi.util.resetApiState());
  store.dispatch(roommatesApi.util.resetApiState());
  store.dispatch(animalTrainingApi.util.resetApiState());
  store.dispatch(docPacketApi.util.resetApiState());
  store.dispatch(commonApi.util.resetApiState());
  store.dispatch(propertyConfigSetApi.util.resetApiState());
  store.dispatch(paymentApi.util.resetApiState());
  store.dispatch(faqApi.util.resetApiState());
  store.dispatch(curriculumApi.util.resetApiState());
  store.dispatch(tenantApi.util.resetApiState());
  store.dispatch(reportingApi.util.resetApiState());
  store.dispatch(ssoApi.util.resetApiState());
  store.dispatch(partnerApiIntegrationsApi.util.resetApiState());
  store.dispatch(scimApi.util.resetApiState());
};

export type RootState = ReturnType<typeof rootReducer>;
type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
setupListeners(store.dispatch);

export type QueryReturnValue<T = unknown, E = unknown, M = unknown> =
  | {
      error: E;
      data?: undefined;
      meta?: M;
    }
  | {
      error?: undefined;
      data: T;
      meta?: M;
    };

export type BaseQueryApi = {
  signal: AbortSignal;
  dispatch: ThunkDispatch<any, any, any>;
  getState: () => unknown;
};
export type MaybePromise<T> = T | PromiseLike<T>;

export type BaseQueryFn<
  Args = any,
  Result = unknown,
  Error = unknown,
  DefinitionExtraOptions = {
    /* do nothing */
  },
  Meta = {
    /* do nothing */
  }
> = (
  args: Args,
  api: BaseQueryApi,
  extraOptions: DefinitionExtraOptions
) => MaybePromise<QueryReturnValue<Result, Error, Meta>>;

export type BaseQuery = (
  arg: string | FetchArgs
) => MaybePromise<QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>>;
